import {
  ApplicationConfig,
  EnvironmentProviders,
  importProvidersFrom,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Routes } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import {
  HammerModule,
  HAMMER_GESTURE_CONFIG,
  HAMMER_LOADER,
  provideClientHydration,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APIInterceptor } from '@shared/interceptors/api.interceptor';

import { DefaultPreset } from '@shared/styles/default.preset';
import { PRESET_THEME } from './models/enums/PresetTheme.enum';
import { RoutesService } from './services/routes.service';
import { getRoutes } from './app.routes';
import { HammerConfig } from './configs/hammer-config';
import { provideStore } from '@ngrx/store';
import { infiniteScrollReducer } from './stores/infiniteScroll/infinite-scroll.reducers';
import { ErrorService } from '@shared/services/error.service';
import { MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import { GRecaptchaInterceptor } from './interceptors/grecaptcha.interceptor';
import {
  APP_CONFIG,
  ENVIRONMENT,
  NAV_ITEMS,
  ROUTES,
  THEMES,
} from '@shared/tokens';
import { APP_CONFIG_VALUE } from './config/APP_CONFIG';
import THEMES_VALUE from './constants/THEMES';
import { ROUTES as ROUTES_VALUE } from './models/enums/Routes.enum';
export function getRoutesFromService(routesService: RoutesService): Routes {
  return Object.values(routesService.ROUTES);
}

declare global {
  interface Window {
    enableTestMode: () => void;
    disableTestMode: () => void;
  }
}

// Base providers that work for both client and server
const getBaseProviders = (): (EnvironmentProviders | Provider)[] => [
  { provide: ENVIRONMENT, useValue: environment },
  { provide: APP_CONFIG, useValue: APP_CONFIG_VALUE },
  { provide: NAV_ITEMS, useValue: [] },
  { provide: THEMES, useValue: THEMES_VALUE },
  { provide: ROUTES, useValue: ROUTES_VALUE },
  provideZoneChangeDetection({ eventCoalescing: true }),
  provideStore({ infiniteScroll: infiniteScrollReducer }),
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  importProvidersFrom(HttpClient),
  MessageService,
  ErrorService,
  { provide: HTTP_INTERCEPTORS, useClass: APIInterceptor, multi: true },
  provideAnimationsAsync(),

  providePrimeNG({
    theme: {
      preset: DefaultPreset(PRESET_THEME.Aura),
      options: {
        darkModeSelector: false || 'none',
      },
    },
  }),
  provideRouter(getRoutes()),
  provideClientHydration(),
];

// CLIENT-ONLY configuration (used by main.ts)
export const getAppConfig = (): ApplicationConfig => {
  window.enableTestMode = () => {
    localStorage.setItem('TEST_MODE', 'true');
    location.reload();
  };
  window.disableTestMode = () => {
    localStorage.removeItem('TEST_MODE');
    location.reload();
  };
  const providers = [
    ...getBaseProviders(),
    provideStoreDevtools(),
    importProvidersFrom(HammerModule),
    { provide: HAMMER_LOADER, useValue: () => import('hammerjs') },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
  ];

  if (environment.GRECAPTCHA_ENABLED) {
    providers.push({
      provide: HTTP_INTERCEPTORS,
      useClass: GRecaptchaInterceptor,
      multi: true,
    });
  }

  //TODO service worker wyłączony bo interceptuje mi błędy 504
  // providers.push(
  //   provideServiceWorker('ngsw-worker.js', {
  //     enabled: false,
  //     registrationStrategy: 'registerImmediately',
  //   })
  // );

  return {
    providers: providers,
  };
};

// SERVER-ONLY configuration (used by app.config.server.ts) //NO SERVICE WORKER!
export const getServerConfig = (): ApplicationConfig => {
  const providers = [...getBaseProviders()];

  return {
    providers: providers,
  };
};
